// Keep existing stored values: standard is glow, rich is liquid glass.
export type VisualEffectsLevel =
  "off" | "lite" | "standard" | "frosted" | "rich";

export const visualEffectsKey = "ark-visual-effects";
const visualEffectsEvent = "ark:visual-effects-change";
const defaultLevel: VisualEffectsLevel = "standard";

type EffectsWindow = Window & {
  __arkVisualEffects?: { level: VisualEffectsLevel; volatile: boolean };
};

export function isVisualEffectsLevel(
  value: unknown,
): value is VisualEffectsLevel {
  return (
    value === "off" ||
    value === "lite" ||
    value === "standard" ||
    value === "frosted" ||
    value === "rich"
  );
}

// The window cache also covers independent copies loaded by federated apps.
export function readVisualEffects(): VisualEffectsLevel {
  if (typeof window === "undefined") return defaultLevel;
  const cache = (window as EffectsWindow).__arkVisualEffects;
  if (cache?.volatile) return cache.level;
  try {
    const value = window.localStorage.getItem(visualEffectsKey);
    return isVisualEffectsLevel(value) ? value : defaultLevel;
  } catch {
    return cache?.level ?? defaultLevel;
  }
}

export function setVisualEffects(level: VisualEffectsLevel) {
  if (typeof window === "undefined" || !isVisualEffectsLevel(level)) return;
  let volatile = false;
  try {
    window.localStorage.setItem(visualEffectsKey, level);
  } catch {
    volatile = true;
  }
  (window as EffectsWindow).__arkVisualEffects = { level, volatile };
  window.dispatchEvent(new Event(visualEffectsEvent));
}

export function subscribeVisualEffects(listener: () => void) {
  if (typeof window === "undefined") return () => undefined;
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== visualEffectsKey) return;
    delete (window as EffectsWindow).__arkVisualEffects;
    listener();
  };
  window.addEventListener(visualEffectsEvent, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(visualEffectsEvent, listener);
    window.removeEventListener("storage", onStorage);
  };
}
